import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { SharedModule } from '@aiwyn/shared';
import { SafeDatePipe } from '@aiwyn/shared/pipes/safe-date/safe-date.pipe';
import { CrlActivityEntry, CrlActivityFile, CrlAvatarColorKey, CrlClientContact, CrlTeamMember } from '../../../../models';
import { getCxpContactInitials, pickAvatarColorKey } from '../../../../shared';
import { CrlPanelActivityComponent } from '../crl-panel-activity/crl-panel-activity.component';

/**
 * Overlay right rail: a Details card (Due on, Requested by, Client access,
 * Privacy) and a Latest Activity card with a "View All Activity" affordance.
 * Both are collapsible cards (Figma node 12019:14668). Per the design there is
 * NO Checklist section here (out of scope).
 *
 * Presentational — the team/contacts/activity are pre-resolved by the
 * container's selectors (real names via the contact directory), and the
 * signed-in contact id is passed in via `currentContactId` (the container owns
 * auth). Reuses the shared pastel avatar palette + contact-initials util so
 * there is no bespoke avatar/colour logic here.
 */
@Component({
  selector: 'cxp-crl-overlay-details-rail',
  templateUrl: './crl-overlay-details-rail.component.html',
  styleUrls: ['./crl-overlay-details-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, SafeDatePipe, CrlPanelActivityComponent],
})
export class CrlOverlayDetailsRailComponent {
  /** Selected item's due date for the "Due on" line; null hides the row. */
  readonly dueDate = input<string | null>(null);
  /** Firm team — the first member (owner) is the "Requested by" person. */
  readonly team = input<CrlTeamMember[]>([]);
  /** Client-access recipients (resolved names where available). */
  readonly clientContacts = input<CrlClientContact[]>([]);
  /** True when the item is access-restricted (drives the Privacy line). */
  readonly isRestricted = input<boolean>(false);
  /**
   * Whether the "Invite" affordance is shown. False for an audit view (a masked
   * restricted item), where the caller may see the item exists but must not
   * manage its access.
   */
  readonly canInvite = input<boolean>(true);
  /** Whether removal is offered on this item at all; {@link canRemove} decides per row. */
  readonly canRemoveAccess = input<boolean>(false);
  /** True when the viewer is the client owner (they act on others, not themselves). */
  readonly isViewerOwner = input<boolean>(false);
  /**
   * Every contact id linked to the signed-in user — the rows that get the "YOU"
   * pill. It is an ARRAY, not a single id, because a user can have multiple
   * contact records (login picks the first, which is often NOT the one assigned
   * to this request). The set is resolved server-side by email match
   * (`AppState.getUserContactIds`), so any of the user's contacts matches.
   */
  readonly currentContactIds = input<number[]>([]);
  /** Activity entries for the Latest Activity timeline. */
  readonly activity = input<CrlActivityEntry[]>([]);
  /** True while the item's activity is loading (feed shows a loading line). */
  readonly activityLoading = input<boolean>(false);

  readonly viewAllActivity = output<void>();
  /** "Invite" link in the Client access header — opens the invite dialog (container owns it). */
  readonly invite = output<void>();
  /** Remove one person's access to this item (container owns the mutation). */
  readonly removeContact = output<CrlClientContact>();
  /** Preview an activity file chip (bubbled to the container's previewer). */
  readonly previewActivityFile = output<CrlActivityFile>();
  /** Download an activity file chip (bubbled to the container). */
  readonly downloadActivityFile = output<CrlActivityFile>();

  /** Card collapse state (both start expanded). */
  protected readonly detailsExpanded = signal(true);
  protected readonly activityExpanded = signal(true);

  /** "Requested by" = the firm owner (starred, first), falling back to the first member. */
  protected readonly requestedBy = computed<CrlTeamMember | null>(() => {
    const members = this.team();
    return members.find((member) => member.isOwner) ?? members[0] ?? null;
  });

  toggleDetails(): void {
    this.detailsExpanded.update((open) => !open);
  }

  toggleActivity(): void {
    this.activityExpanded.update((open) => !open);
  }

  initialsForContact(contact: CrlClientContact): string {
    return getCxpContactInitials(contact.name, contact.email);
  }

  initialsForMember(member: CrlTeamMember): string {
    return getCxpContactInitials(member.name, member.email);
  }

  /** Deterministic pastel key for a client person's avatar — contacts seed off contactId, guests off secureLinkId. */
  contactAvatarColor(contact: CrlClientContact): CrlAvatarColorKey {
    return pickAvatarColorKey(`#${contact.contactId ?? contact.secureLinkId}`);
  }

  /** Deterministic pastel key for a firm member's avatar (seeded off its id). */
  memberAvatarColor(member: CrlTeamMember): CrlAvatarColorKey {
    return pickAvatarColorKey(`#${member.staffId}`);
  }

  /** True for a contact row belonging to the signed-in user (drives the "YOU" pill). */
  isCurrentUser(contact: CrlClientContact): boolean {
    return contact.contactId != null && this.currentContactIds().includes(contact.contactId);
  }

  /**
   * The owner acts on everyone but themselves, an assignee only on their own row.
   * The last remaining person can never be removed — an item with nobody on it
   * has no route back.
   */
  canRemove(contact: CrlClientContact): boolean {
    if (!this.canRemoveAccess() || this.clientContacts().length <= 1) return false;
    return this.isViewerOwner() ? !this.isCurrentUser(contact) : this.isCurrentUser(contact);
  }

  contactFallbackLabel(contact: CrlClientContact): string {
    if (contact.email) return contact.email;
    return contact.contactId != null ? `Contact #${contact.contactId}` : 'Invited guest';
  }
}
